import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useThemeMode } from '@/contexts/ThemeContext';
import { AnimatedLogo } from '@/components/AnimatedLogo';

const navItems = [
  { label: 'Terms', href: '/terms', external: false },
  { label: 'Privacy', href: '/privacy', external: false },
  { label: 'FAQ', href: '/faq', external: false },
];

export const Header = () => {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={0}>
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 2, minHeight: { xs: 64, sm: 72 } }}>
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                flexGrow: isMobile ? 1 : 0,
              }}
            >
              <AnimatedLogo size={40} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: 'text.primary',
                }}
              >
                Calorium
              </Typography>
            </Box>

            {isMobile ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton
                  color="inherit"
                  aria-label="toggle theme"
                  onClick={toggleTheme}
                >
                  <Icon icon={mode === 'dark' ? 'solar:sun-bold' : 'solar:moon-bold'} width={24} />
                </IconButton>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <Icon icon="solar:hamburger-menu-bold" width={24} />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  ml: 'auto',
                }}
              >
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    component={item.external ? 'a' : Link}
                    to={item.external ? undefined : item.href}
                    href={item.external ? item.href : undefined}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      px: 2,
                      '&:hover': {
                        color: 'text.primary',
                        backgroundColor: 'grey.50',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <IconButton
                  color="inherit"
                  aria-label="toggle theme"
                  onClick={toggleTheme}
                  sx={{ ml: 1 }}
                >
                  <Icon icon={mode === 'dark' ? 'solar:sun-bold' : 'solar:moon-bold'} width={24} />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
            Calorium
          </Typography>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  component={item.external ? 'a' : Link}
                  to={item.external ? undefined : item.href}
                  href={item.external ? item.href : undefined}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  onClick={handleDrawerToggle}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};
